import type { VisualSignalBus } from '../signals/VisualSignalBus.js';
import type { AudioFeatureTimeline } from './AudioFeatures.js';

/**
 * Produces the song's half of the signals, offline.
 *
 * The live source is untouched — this is a sibling, not a replacement, and both
 * write to the same bus through the same vocabulary. What differs is where the
 * numbers come from: the live one asks an `AnalyserNode` what is playing right
 * now, this one reads a timeline that was computed before rendering began.
 *
 * The beat grid is the interesting part. Live, beats are announced as they are
 * noticed, and a long frame can arrive after several have passed — Phase 4 found
 * that capping the catch-up silently dropped them. Offline there is no such
 * thing as noticing late: the frame's exact span is known, so **every** beat
 * inside it is emitted, with no cap and no possibility of a gap. A 24 fps render
 * of a 175 BPM song emits exactly the beats a 120 fps render does.
 */

export interface OfflineSignalSourceOptions {
  readonly bus: VisualSignalBus;
  readonly beatTimes: readonly number[];
  readonly bpm: number;
  readonly firstBeatOffsetSeconds?: number;
  readonly beatsPerBar?: number;
  readonly barsPerSection?: number;
  readonly features?: AudioFeatureTimeline | null;
}

export class OfflineSignalSource {
  private readonly bus: VisualSignalBus;
  private readonly beatTimes: readonly number[];
  private readonly bpm: number;
  private readonly firstBeatOffset: number;
  private readonly beatsPerBar: number;
  private readonly barsPerSection: number;
  private features: AudioFeatureTimeline | null;

  /** Highest beat index already announced. -1 before the first. */
  private lastBeatIndex = -1;
  private emittedBeats = 0;
  private emittedBars = 0;
  private emittedSections = 0;
  private emittedOnsets = 0;

  constructor(options: OfflineSignalSourceOptions) {
    this.bus = options.bus;
    this.beatTimes = options.beatTimes;
    this.bpm = options.bpm > 0 ? options.bpm : 0;
    this.firstBeatOffset = options.firstBeatOffsetSeconds ?? 0;
    this.beatsPerBar = options.beatsPerBar ?? 4;
    this.barsPerSection = options.barsPerSection ?? 8;
    this.features = options.features ?? null;
  }

  setFeatures(features: AudioFeatureTimeline | null): void {
    this.features = features;
  }

  reset(): void {
    this.lastBeatIndex = -1;
    this.emittedBeats = 0;
    this.emittedBars = 0;
    this.emittedSections = 0;
    this.emittedOnsets = 0;
    this.bus.setLevel('BPM', this.bpm);
  }

  /**
   * Everything the song contributes to one frame.
   *
   * `from` is the previous frame's time and `to` is this one's, so the pair
   * describes a span rather than an instant — which is what makes "emit every
   * beat in the frame" expressible at all.
   */
  sampleFrame(from: number, to: number): void {
    this.bus.setLevels({ TIME: to, BPM: this.bpm });

    if (this.features !== null) {
      const bands = this.features.sample(to);
      this.bus.setLevels({
        AMPLITUDE: bands.amplitude,
        BASS: bands.bass,
        MID: bands.mid,
        TREBLE: bands.treble,
      });
      // The strongest transient anywhere in the frame's span, so nothing is
      // lost between frames however low the frame rate.
      const onset = this.features.onsetsBetween(from, to);
      if (onset > 0) {
        this.bus.emit({ name: 'ONSET', time: to, strength: onset });
        this.emittedOnsets += 1;
      }
    }

    this.emitBeatsUpTo(to);
  }

  /**
   * Emits every beat at or before `time` that has not been emitted.
   *
   * No cap. Offline, a long frame is not a stall to be recovered from — it is
   * simply a frame that covers more music, and every beat inside it belongs to
   * it. This is the guarantee §6 asks for, and it is why the offline render at
   * any frame rate produces the same beat count.
   */
  private emitBeatsUpTo(time: number): void {
    const target = this.beatIndexAt(time);
    if (target < 0 || target === this.lastBeatIndex) return;

    for (let beat = this.lastBeatIndex + 1; beat <= target; beat += 1) {
      const beatTime = this.beatTimeAt(beat);
      this.bus.emit({ name: 'BEAT', time: beatTime, strength: 1, detail: { index: beat } });
      this.emittedBeats += 1;

      if (beat % this.beatsPerBar === 0) {
        const bar = beat / this.beatsPerBar;
        this.bus.emit({ name: 'BAR', time: beatTime, strength: 1, detail: { index: bar } });
        this.emittedBars += 1;
        if (bar % this.barsPerSection === 0) {
          this.bus.emit({
            name: 'SECTION',
            time: beatTime,
            strength: 1,
            detail: { index: bar / this.barsPerSection },
          });
          this.emittedSections += 1;
        }
      }
    }
    this.lastBeatIndex = target;
  }

  private beatIndexAt(time: number): number {
    if (this.beatTimes.length > 0) {
      // Binary search rather than the live source's forward walk: offline the
      // clock may be seeked, so "where we were" is not a useful starting point.
      let low = 0;
      let high = this.beatTimes.length - 1;
      let found = -1;
      while (low <= high) {
        const middle = (low + high) >>> 1;
        if ((this.beatTimes[middle] as number) <= time) {
          found = middle;
          low = middle + 1;
        } else {
          high = middle - 1;
        }
      }
      return found;
    }
    if (this.bpm <= 0) return -1;
    const elapsed = time - this.firstBeatOffset;
    return elapsed < 0 ? -1 : Math.floor((elapsed * this.bpm) / 60);
  }

  private beatTimeAt(index: number): number {
    const detected = this.beatTimes[index];
    if (detected !== undefined) return detected;
    if (this.bpm <= 0) return 0;
    return this.firstBeatOffset + (index * 60) / this.bpm;
  }

  diagnostics(): {
    readonly beats: number;
    readonly bars: number;
    readonly sections: number;
    readonly onsets: number;
    readonly hasFeatures: boolean;
  } {
    return {
      beats: this.emittedBeats,
      bars: this.emittedBars,
      sections: this.emittedSections,
      onsets: this.emittedOnsets,
      hasFeatures: this.features !== null,
    };
  }
}
